import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  LinearProgress,
  Link as MuiLink,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { FleetTachoRow } from '@oip/contracts';
import { AdminPageLayout } from '../components/common/AdminPageLayout';
import { DetailCard } from '../components/common/DetailCard';
import { cardIcon } from '../components/common/cardIcons';
import { EmptyState } from '../components/common/EmptyState';
import { ErrorPanel } from '../components/common/ErrorPanel';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import { useFleetTacho, useTachoActivity, useTachoVehicles } from '../hooks/api';
import { TachoActivityCard } from './vehicles/TachoActivityCard';
import { relative } from '../utils/format';
import {
  breakProgress,
  breakStatus,
  formatMinutes,
  limitProgress,
} from './vehicles/tachoLanguage';
import { useTerm } from '../units/useTerm';

/**
 * Drivers' hours across the fleet.
 *
 * The question the per-vehicle card cannot answer without opening every vehicle
 * in turn: *who has to stop soon*. Asked for as "can we have a menu that shows
 * tacho related data".
 *
 * ⚠️ **Nothing here is an infringement, and nothing is thresholded.** These are
 * the DEVICE's own counters; whether any of them breaches anything depends on
 * the ruleset the vehicle runs under, which OIP is not told
 * (`tachograph-parameters.md` section 8, Q5). Sorting by urgency is a reading
 * ORDER, not a verdict, and the page says so rather than leaving an absent
 * warning to be read as compliance.
 *
 * The wording and the break arithmetic are `tachoLanguage.ts`, shared with the
 * vehicle card — the same quantity on two screens must not look like two
 * things, and the break limit is recovered from the device's own two numbers
 * either way.
 */
export function FleetTachoPage(): JSX.Element {
  // The reader's own word for a plate or a licence. See `useTerm`.
  const { t } = useTerm();
  const query = useFleetTacho();
  const d = query.data;
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  /*
   * ⚠️ **The picker has its OWN list, over the CHART's window — not the table's.**
   * The table above is a fixed 24 hours (*who has to stop soon*); sourcing the
   * picker from it hid the whole section on any fleet whose devices had been
   * quiet for a day, which is exactly when somebody wants to look back. Found
   * by driving it against a development fleet whose tacho data was two days
   * old. Most vehicles carry no tachograph at all, so this is a real filter
   * rather than the vehicle list in disguise.
   */
  const fitted = useTachoVehicles(days);
  const chosen = fitted.data?.items.find((r) => r.vehicleId === vehicleId) ?? null;
  const activity = useTachoActivity(chosen?.vehicleId ?? null, days);

  return (
    <AdminPageLayout
      title="Drivers’ hours"
      description="The latest tachograph reading from each vehicle that has one, soonest limit first — whichever of the break and the daily rest falls sooner."
    >
      {query.isLoading && <LoadingSkeleton rows={6} height={44} />}
      {query.isError && <ErrorPanel error={query.error} onRetry={query.refetch} />}

      {d && d.items.length === 0 && (
        /*
         * ⚠️ WHICH nothing. No vehicle carrying a tachograph is the ordinary
         * case on most fleets and is not a gap to chase; it is a different fact
         * from a fleet that has stopped reporting, and a shared empty state
         * would send somebody looking for a fault that is not there.
         */
        <EmptyState
          title="No tachograph data"
          message={
            d.activeVehicles > 0
              ? `None of this fleet’s ${d.activeVehicles} active vehicles has sent tachograph data in the last ${d.windowHours} hours. Most vehicles do not carry one, so this is usually normal rather than something to chase.`
              : 'This fleet has no active vehicles.'
          }
        />
      )}

      {d && d.items.length > 0 && (
        <DetailCard
          icon={cardIcon('schedule')}
          title="By vehicle"
          subtitle={`${d.items.length} of ${d.activeVehicles} active vehicles reported a tachograph in the last ${d.windowHours} hours`}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Driver 1</TableCell>
                  <TableCell>Before a break</TableCell>
                  <TableCell>Before a daily rest</TableCell>
                  <TableCell align="right">Driven today</TableCell>
                  <TableCell align="right">This week</TableCell>
                  <TableCell>Reported</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {d.items.map((r) => (
                  <Row key={r.vehicleId} row={r} />
                ))}
              </TableBody>
            </Table>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            These are each tachograph’s own figures, shown as sent. OIP does not judge them
            against a drivers’ hours ruleset, so nothing here says a driver is or is not
            compliant. Slot 1 only — a crewed vehicle is marked, and its second driver is on the vehicle’s own
            page.
          </Typography>
        </DetailCard>
      )}

      {/*
        ⚠️ Rendered on its OWN evidence, never on the table's. The table is 24
        hours and this is up to a month: gating one on the other is what made
        the section vanish on a fleet with a fortnight of history to show.
      */}
      {(fitted.data?.items.length ?? 0) > 0 && (
        /*
         * ⚠️ **LAST, under its own heading, with the picker beside it.** Both
         * halves of that are the same fix CAN operations already made after
         * "why is one vehicle on a fleet dashboard?": a per-vehicle panel
         * surrounded by fleet panels reads as another fleet panel, and a
         * control in the page header reads as the page having DECIDED rather
         * than as a selection somebody made.
         */
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            One vehicle, day by day
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The table above is the latest reading from every vehicle. This is one vehicle’s
            recorded activity laid out on a clock — what the tachograph actually captured,
            shift by shift.{' '}
            {/*
              ⚠️ The DENOMINATOR, for the reason the table carries one: a short
              list on a fleet of a hundred reads as a fleet that has stopped
              reporting, when most vehicles simply have no tachograph fitted.
            */}
            {fitted.data &&
              `${fitted.data.items.length} of ${fitted.data.activeVehicles} active vehicles recorded one in the last ${fitted.data.days} days.`}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 280 }}
              options={fitted.data?.items ?? []}
              value={chosen}
              onChange={(_, v) => setVehicleId(v?.vehicleId ?? null)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.vehicleId === b.vehicleId}
              /*
               * Searches the REGISTRATION as well as the name: a plate is what
               * is painted on the vehicle and said over a radio, and matching
               * the name alone fails exactly the person in a hurry.
               */
              filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter(
                  (o) =>
                    o.name.toLowerCase().includes(q) || o.registration.toLowerCase().includes(q),
                );
              }}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.vehicleId}>
                  <Box>
                    <Typography variant="body2">{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.registration} · last recorded {relative(o.lastAt)}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Vehicle" placeholder={`Name or ${t('registration')}`} />
              )}
            />
            <TextField
              select
              size="small"
              label="Days"
              sx={{ minWidth: 130 }}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              {[3, 7, 14, 28].map((n) => (
                <MenuItem key={n} value={n}>
                  {n} days
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {/*
            ⚠️ Nothing is fetched until a vehicle is chosen, and the prompt is a
            sentence rather than an empty card. A default vehicle would be the
            page deciding, which is the thing this layout exists to avoid.
          */}
          {!chosen && (
            <Typography variant="body2" color="text.secondary">
              Choose a vehicle to see its recorded activity.
            </Typography>
          )}
          {chosen && activity.isLoading && <LoadingSkeleton rows={4} height={40} />}
          {chosen && activity.isError && (
            <ErrorPanel error={activity.error} onRetry={activity.refetch} />
          )}
          {chosen && activity.data && (
            <TachoActivityCard
              data={activity.data}
              subtitle={`${chosen.name} · ${chosen.registration} · ${activity.data.days.length} days to today`}
            />
          )}
        </Box>
      )}
    </AdminPageLayout>
  );
}

function Row({ row }: { row: FleetTachoRow }): JSX.Element {
  const progress = breakProgress(row);
  // ⚠️ The SHARED reading, not a local branch. The dashboard card renders the
  // same three states from the same function, so they cannot drift apart.
  const status = breakStatus(row);
  const dailyRest = formatMinutes(row.timeToDailyRestMin);
  const dailyProgress = limitProgress(row.dailyDriveMin, row.timeToDailyRestMin);

  return (
    <TableRow hover>
      <TableCell>
        <MuiLink component={RouterLink} to={`/vehicles/${row.vehicleId}`} underline="hover">
          {row.name}
        </MuiLink>
        <Typography variant="caption" color="text.secondary" display="block">
          {row.registration}
        </Typography>
      </TableCell>

      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          {row.workStateLabel && (
            <Chip size="small" variant="outlined" label={row.workStateLabel} />
          )}
          {/*
            ⚠️ Only when the card is ABSENT, and only as a warning rather than
            an accusation: the provider collapses the device's four-valued state
            upstream, so this cannot tell "no card" from "privacy mode". A chip
            on every row saying "card inserted" would also be a column of one
            value, which teaches a reader to stop looking at it.
          */}
          {row.cardPresent === false && <Chip size="small" color="warning" label="No card" />}
          {row.overSpeed === true && <Chip size="small" color="error" label="Over speed" />}
          {/*
            ⚠️ Stated, never judged. The device says it did not recognise the
            card's driver and does not say why — an agency driver, a card from
            another operator, a fault. Anything stronger would accuse a named
            person on evidence the provider never sent.
          */}
          {row.driverRecognised === false && (
            <Chip size="small" color="warning" variant="outlined" label="Driver not recognised" />
          )}
          {/*
            ⚠️ The row is SLOT 1, so on a crewed vehicle it is half the story.
            Saying so is the difference between a reader knowing to open the
            vehicle and never learning there was a second driver at all.
          */}
          {row.hasSecondDriver && <Chip size="small" variant="outlined" label="2 drivers" />}
        </Stack>
      </TableCell>

      <TableCell sx={{ minWidth: 180 }}>
        {/*
          ⚠️ A driver at REST reads "On a break", not "0 m". Both come from the
          same number and they say opposite things: "0 m before a break" on
          somebody who has already stopped is arithmetically true and reads as
          the most urgent row on the page. They sort below the driving rows for
          the same reason.
        */}
        {status.kind === 'RESTING' ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {status.label}
            </Typography>
            {/*
              ⚠️ THE MISSING HALF OF "On a break". The page could say a driver
              had stopped and not how far through the stop they were, which is
              the question anybody asks next — when they are available again.
              Only for a driver who IS resting: zero minutes of break on
              somebody driving is the counter at rest, not a fact worth showing.
            */}
            {formatMinutes(row.breakMin) && (
              <Typography variant="caption" color="text.secondary" display="block">
                {formatMinutes(row.breakMin)} taken
              </Typography>
            )}
          </>
        ) : status.kind === 'UNKNOWN' ? (
          // Not reported, never 0 — zero means "stop now", and this row sorted
          // last precisely because it is unknown rather than urgent.
          <Typography variant="body2" color="text.disabled">
            {status.label}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {status.label}
            </Typography>
            {progress && (
              <LinearProgress
                variant="determinate"
                value={progress.pct}
                // Amber past three quarters: a nudge about when to plan a stop,
                // not a compliance verdict — the limit is the device's own.
                color={progress.pct >= 75 ? 'warning' : 'primary'}
                sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
              />
            )}
          </>
        )}
      </TableCell>

      {/*
        ⚠️ The SECOND limit, drawn the same honest way: its denominator is
        `dailyDriveMin + timeToDailyRestMin`, which is the daily limit the
        device is applying, never a 9-hour constant chosen here. A fleet under
        GB domestic rules gets its own limit for free.
      */}
      <TableCell sx={{ minWidth: 150 }}>
        {dailyRest === null ? (
          <Typography variant="body2" color="text.disabled">
            Not reported
          </Typography>
        ) : (
          <>
            <Typography variant="body2">{dailyRest}</Typography>
            {dailyProgress && (
              <LinearProgress
                variant="determinate"
                value={dailyProgress.pct}
                color={dailyProgress.pct >= 75 ? 'warning' : 'primary'}
                sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
              />
            )}
          </>
        )}
      </TableCell>

      <TableCell align="right">
        {formatMinutes(row.dailyDriveMin) ?? (
          <Typography component="span" variant="body2" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {formatMinutes(row.weeklyDriveMin) ?? (
          <Typography component="span" variant="body2" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {relative(row.reportedAt)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
